import { useCallback, useEffect, useState } from 'react';
import { api, errorText, formatDate, formatQty } from '../api';
import { useAuth } from '../auth';
import { Badge, Card, CheckField, Empty, Loading, Notice, TextField } from '../components/ui';
import type {
  Article,
  InboundLine,
  InboundOrder,
  InboundStatus,
  Location,
  Partner,
  PutawaySuggestion,
  Warehouse,
} from '../types';

const STATUS_STYLE: Record<InboundStatus, 'warn' | 'info' | 'ok' | 'default'> = {
  angekuendigt: 'warn',
  teilweise: 'info',
  abgeschlossen: 'ok',
  storniert: 'default',
};

const STATUS_LABEL: Record<InboundStatus, string> = {
  angekuendigt: 'angekündigt',
  teilweise: 'teilweise angenommen',
  abgeschlossen: 'abgeschlossen',
  storniert: 'storniert',
};

/** Kleine Artikelsuche für die Positionserfassung. */
function ArticlePicker({ ownerId, onPick }: { ownerId: string; onPick: (article: Article) => void }) {
  const [term, setTerm] = useState('');
  const [hits, setHits] = useState<Article[]>([]);

  useEffect(() => {
    if (!term.trim()) {
      setHits([]);
      return;
    }
    const timer = setTimeout(() => {
      api<Article[]>('/api/articles/lookup', { query: { q: term.trim() } })
        .then((found) => setHits(ownerId ? found.filter((a) => a.owner_id === ownerId) : found))
        .catch(() => setHits([]));
    }, 200);
    return () => clearTimeout(timer);
  }, [term, ownerId]);

  return (
    <>
      <label className="field">
        <span>Artikel suchen</span>
        <input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Artikelnummer, EAN oder Bezeichnung"
        />
      </label>
      {hits.length > 0 && (
        <div className="pick-list">
          {hits.map((hit) => (
            <button
              key={hit.id}
              onClick={() => {
                onPick(hit);
                setTerm('');
                setHits([]);
              }}
            >
              <strong>{hit.name}</strong>
              <div className="small muted mono">
                {hit.sku} · {hit.owner_name}
              </div>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

interface DraftLine {
  article: Article;
  qtyExpected: string;
  lotCode: string;
  bestBefore: string;
}

export function InboundPage() {
  const { owners, ownerId: globalOwner, can } = useAuth();
  const mayCreate = can('admin', 'kaufmann', 'disponent');
  const mayReceive = can('admin', 'schichtleiter', 'lagerist', 'disponent');

  const [orders, setOrders] = useState<InboundOrder[] | null>(null);
  const [openOnly, setOpenOnly] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<{ order: InboundOrder; lines: InboundLine[] } | null>(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [suppliers, setSuppliers] = useState<Partner[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);

  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState({
    ownerId: globalOwner,
    supplierId: '',
    warehouseId: '',
    reference: '',
    expectedAt: '',
    note: '',
  });
  const [draftLines, setDraftLines] = useState<DraftLine[]>([]);

  // Annahmeformular je Position
  const [receiveFor, setReceiveFor] = useState<string | null>(null);
  const [receive, setReceive] = useState({ qty: '', locationCode: '', lotCode: '', bestBefore: '', serial: '' });
  const [suggestion, setSuggestion] = useState<PutawaySuggestion | null>(null);

  const load = useCallback(async () => {
    setError('');
    try {
      setOrders(
        await api<InboundOrder[]>('/api/inbound/orders', {
          query: { openOnly: openOnly ? 'true' : undefined },
        }),
      );
    } catch (err) {
      setError(errorText(err));
    }
  }, [openOnly]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    api<Warehouse[]>('/api/warehouse').then(setWarehouses).catch(() => setWarehouses([]));
    api<Partner[]>('/api/partners', { query: { role: 'supplier' } })
      .then(setSuppliers)
      .catch(() => setSuppliers([]));
    api<Location[]>('/api/warehouse/locations').then(setLocations).catch(() => setLocations([]));
  }, []);

  const loadDetail = useCallback(async (id: string) => {
    setDetail(null);
    try {
      setDetail(await api<{ order: InboundOrder; lines: InboundLine[] }>(`/api/inbound/orders/${id}`));
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  useEffect(() => {
    if (openId) void loadDetail(openId);
  }, [openId, loadDetail]);

  async function createOrder() {
    if (draftLines.length === 0) return setError('Mindestens eine Position wird gebraucht.');
    setBusy(true);
    setError('');
    try {
      const created = await api<InboundOrder>('/api/inbound/orders', {
        method: 'POST',
        body: {
          ownerId: draft.ownerId,
          supplierId: draft.supplierId || null,
          warehouseId: draft.warehouseId,
          reference: draft.reference || null,
          expectedAt: draft.expectedAt || null,
          note: draft.note || null,
          lines: draftLines.map((line) => ({
            articleId: line.article.id,
            qtyExpected: Number(line.qtyExpected.replace(',', '.')),
            lotCode: line.lotCode || null,
            bestBefore: line.bestBefore || null,
          })),
        },
      });
      setMessage(`Avis ${created.number} angelegt.`);
      setShowForm(false);
      setDraftLines([]);
      setDraft({ ...draft, reference: '', expectedAt: '', note: '' });
      await load();
      setOpenId(created.id);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function startReceive(line: InboundLine) {
    setReceiveFor(line.id);
    setSuggestion(null);
    const open = Math.max(0, line.qty_expected - line.qty_received);
    setReceive({
      qty: String(open),
      locationCode: '',
      lotCode: line.lot_code ?? '',
      bestBefore: line.best_before ?? '',
      serial: '',
    });

    if (!detail) return;
    try {
      const found = await api<PutawaySuggestion | null>('/api/inbound/putaway-suggestion', {
        query: {
          articleId: line.article_id,
          ownerId: detail.order.owner_id,
          warehouseId: detail.order.warehouse_id,
        },
      });
      if (found) {
        setSuggestion(found);
        setReceive((current) => ({ ...current, locationCode: found.code }));
      }
    } catch {
      setSuggestion(null);
    }
  }

  async function confirmReceive(line: InboundLine) {
    if (!detail) return;
    const qty = Number(receive.qty.replace(',', '.'));
    if (!Number.isFinite(qty) || qty <= 0) return setError('Bitte eine gültige Menge angeben.');

    const location = locations.find(
      (l) => l.code.toUpperCase() === receive.locationCode.trim().toUpperCase(),
    );
    if (!location) return setError(`Lagerplatz ${receive.locationCode} ist unbekannt.`);

    setBusy(true);
    setError('');
    try {
      const result = await api<{ duplicate: boolean; qtyReceived: number; overDelivery: boolean }>(
        `/api/inbound/orders/${detail.order.id}/lines/${line.id}/receive`,
        {
          method: 'POST',
          body: {
            movementId: crypto.randomUUID(),
            qty,
            toLocationId: location.id,
            lotCode: receive.lotCode || null,
            bestBefore: receive.bestBefore || null,
            serial: receive.serial || null,
          },
        },
      );
      setMessage(
        result.duplicate
          ? 'Diese Annahme war bereits erfasst - es wurde nichts doppelt gebucht.'
          : `${formatQty(qty, line.unit)} ${line.article_name} auf ${location.code} eingelagert.` +
            (result.overDelivery ? ' Achtung: Mehrmenge gegenüber Avis.' : ''),
      );
      setReceiveFor(null);
      await loadDetail(detail.order.id);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function finish(cancel: boolean) {
    if (!detail) return;
    setBusy(true);
    try {
      await api(`/api/inbound/orders/${detail.order.id}/close`, {
        method: 'POST',
        body: { cancel },
      });
      setMessage(cancel ? 'Avis storniert.' : 'Avis abgeschlossen.');
      await loadDetail(detail.order.id);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      <div className="toolbar">
        <CheckField label="Nur offene Avise" checked={openOnly} onChange={setOpenOnly} />
        <span className="spacer" />
        {mayCreate && (
          <button className="primary" onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Abbrechen' : 'Neues Avis'}
          </button>
        )}
      </div>

      {showForm && (
        <Card title="Wareneingang ankündigen">
          <div className="form-grid">
            <label className="field">
              <span>Eigentümer der Ware</span>
              <select
                value={draft.ownerId}
                onChange={(e) => setDraft({ ...draft, ownerId: e.target.value })}
              >
                <option value="">— bitte wählen —</option>
                {owners.map((owner) => (
                  <option key={owner.id} value={owner.id}>
                    {owner.is_self ? `${owner.name} · eigene Ware` : owner.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Lager</span>
              <select
                value={draft.warehouseId}
                onChange={(e) => setDraft({ ...draft, warehouseId: e.target.value })}
              >
                <option value="">— bitte wählen —</option>
                {warehouses.map((warehouse) => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.code} · {warehouse.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Lieferant (optional)</span>
              <select
                value={draft.supplierId}
                onChange={(e) => setDraft({ ...draft, supplierId: e.target.value })}
              >
                <option value="">— keiner —</option>
                {suppliers.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </option>
                ))}
              </select>
            </label>
            <TextField
              label="Referenz des Lieferanten"
              value={draft.reference}
              onChange={(v) => setDraft({ ...draft, reference: v })}
              hint="Lieferschein- oder ASN-Nummer"
            />
            <TextField
              label="Erwartet am"
              type="date"
              value={draft.expectedAt}
              onChange={(v) => setDraft({ ...draft, expectedAt: v })}
            />
            <TextField
              label="Bemerkung"
              value={draft.note}
              onChange={(v) => setDraft({ ...draft, note: v })}
            />
          </div>

          {draft.ownerId ? (
            <ArticlePicker
              ownerId={draft.ownerId}
              onPick={(article) =>
                setDraftLines((current) => [
                  ...current,
                  { article, qtyExpected: '', lotCode: '', bestBefore: '' },
                ])
              }
            />
          ) : (
            <Notice kind="info">Zuerst den Eigentümer wählen, dann lassen sich Positionen erfassen.</Notice>
          )}

          {draftLines.length > 0 && (
            <table style={{ marginTop: 10 }}>
              <thead>
                <tr>
                  <th>Pos</th>
                  <th>Artikel</th>
                  <th className="num">Erwartete Menge</th>
                  <th>Charge</th>
                  <th>MHD</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {draftLines.map((line, index) => (
                  <tr key={`${line.article.id}-${index}`}>
                    <td>{index + 1}</td>
                    <td>
                      <span className="mono">{line.article.sku}</span> {line.article.name}
                    </td>
                    <td className="num" style={{ width: 130 }}>
                      <input
                        style={{ textAlign: 'right' }}
                        value={line.qtyExpected}
                        onChange={(e) =>
                          setDraftLines((current) =>
                            current.map((entry, i) =>
                              i === index ? { ...entry, qtyExpected: e.target.value } : entry,
                            ),
                          )
                        }
                      />
                    </td>
                    <td style={{ width: 140 }}>
                      <input
                        value={line.lotCode}
                        disabled={!line.article.requires_lot}
                        placeholder={line.article.requires_lot ? 'Pflicht' : '—'}
                        onChange={(e) =>
                          setDraftLines((current) =>
                            current.map((entry, i) =>
                              i === index ? { ...entry, lotCode: e.target.value } : entry,
                            ),
                          )
                        }
                      />
                    </td>
                    <td style={{ width: 150 }}>
                      <input
                        type="date"
                        value={line.bestBefore}
                        disabled={!line.article.requires_best_before}
                        onChange={(e) =>
                          setDraftLines((current) =>
                            current.map((entry, i) =>
                              i === index ? { ...entry, bestBefore: e.target.value } : entry,
                            ),
                          )
                        }
                      />
                    </td>
                    <td className="num">
                      <button
                        className="small ghost"
                        onClick={() =>
                          setDraftLines((current) => current.filter((_, i) => i !== index))
                        }
                      >
                        entfernen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="primary" onClick={createOrder} disabled={busy}>
              {busy ? 'Anlegen …' : 'Avis anlegen'}
            </button>
            <button onClick={() => setShowForm(false)}>Abbrechen</button>
          </div>
        </Card>
      )}

      <Card title="Avise" padded={false}>
        {!orders ? (
          <Loading />
        ) : orders.length === 0 ? (
          <Empty>
            {openOnly ? (
              <>
                Keine offenen Avise.{' '}
                <button className="ghost small" onClick={() => setOpenOnly(false)}>
                  Auch abgeschlossene zeigen
                </button>
              </>
            ) : (
              'Keine Avise vorhanden.'
            )}
          </Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Nummer</th>
                <th>Referenz</th>
                <th>Eigentümer</th>
                <th>Lieferant</th>
                <th>Lager</th>
                <th>Erwartet</th>
                <th>Status</th>
                <th className="num">Fortschritt</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id}>
                  <td className="mono">{order.number}</td>
                  <td className="small">{order.reference ?? '—'}</td>
                  <td className="small">{order.owner_name}</td>
                  <td className="small">{order.supplier_name ?? '—'}</td>
                  <td className="mono small">{order.warehouse_code}</td>
                  <td className="small">{formatDate(order.expected_at)}</td>
                  <td>
                    <Badge kind={STATUS_STYLE[order.status]}>{STATUS_LABEL[order.status]}</Badge>
                  </td>
                  <td className="num small">
                    {formatQty(order.qty_received)} / {formatQty(order.qty_expected)}
                  </td>
                  <td className="num">
                    <button className="small ghost" onClick={() => setOpenId(order.id)}>
                      öffnen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {openId && (
        <Card
          title={detail ? `Avis ${detail.order.number}` : 'Avis'}
          actions={
            <>
              {detail && mayReceive && detail.order.status === 'angekuendigt' && (
                <button className="small" onClick={() => finish(true)} disabled={busy}>
                  stornieren
                </button>
              )}
              {detail && mayReceive && detail.order.status === 'teilweise' && (
                <button className="small" onClick={() => finish(false)} disabled={busy}>
                  vorzeitig abschliessen
                </button>
              )}
              <button className="small" onClick={() => setOpenId(null)}>
                schliessen
              </button>
            </>
          }
          padded={false}
        >
          {!detail ? (
            <Loading />
          ) : (
            <>
              <div className="body" style={{ borderBottom: '1px solid var(--line)' }}>
                <div className="small muted">
                  {detail.order.owner_name} · Lager {detail.order.warehouse_code}
                  {detail.order.supplier_name ? ` · Lieferant ${detail.order.supplier_name}` : ''}
                  {detail.order.expected_at ? ` · erwartet ${formatDate(detail.order.expected_at)}` : ''}
                  {detail.order.note ? ` · ${detail.order.note}` : ''}
                </div>
              </div>

              <table>
                <thead>
                  <tr>
                    <th>Pos</th>
                    <th>Artikel</th>
                    <th className="num">Erwartet</th>
                    <th className="num">Angenommen</th>
                    <th className="num">Offen</th>
                    <th>Charge / MHD</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {detail.lines.map((line) => {
                    const open = line.qty_expected - line.qty_received;
                    return (
                      <tr key={line.id}>
                        <td>{line.position}</td>
                        <td>
                          <span className="mono">{line.sku}</span> {line.article_name}
                          {line.requires_lot && (
                            <>
                              {' '}
                              <Badge kind="info">Charge</Badge>
                            </>
                          )}
                        </td>
                        <td className="num">{formatQty(line.qty_expected, line.unit)}</td>
                        <td className="num">{formatQty(line.qty_received)}</td>
                        <td
                          className="num"
                          style={open < 0 ? { color: 'var(--warn)', fontWeight: 600 } : undefined}
                        >
                          {open === 0 ? '—' : formatQty(open)}
                        </td>
                        <td className="small">
                          {line.lot_code ?? '—'}
                          {line.best_before ? ` · ${formatDate(line.best_before)}` : ''}
                        </td>
                        <td className="num">
                          {mayReceive &&
                            detail.order.status !== 'abgeschlossen' &&
                            detail.order.status !== 'storniert' && (
                              <button className="small ghost" onClick={() => startReceive(line)}>
                                annehmen
                              </button>
                            )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {receiveFor && (
                <div className="body" style={{ borderTop: '1px solid var(--line)', background: '#fafbfc' }}>
                  {(() => {
                    const line = detail.lines.find((l) => l.id === receiveFor);
                    if (!line) return null;
                    return (
                      <>
                        <strong>
                          Position {line.position} annehmen — {line.article_name}
                        </strong>
                        {suggestion && (
                          <div className="small muted" style={{ marginTop: 4 }}>
                            Einlagervorschlag: <span className="mono">{suggestion.code}</span> (
                            {suggestion.reason})
                          </div>
                        )}
                        <div className="form-grid" style={{ marginTop: 10 }}>
                          <TextField
                            label={`Menge (${line.unit})`}
                            value={receive.qty}
                            onChange={(v) => setReceive({ ...receive, qty: v })}
                          />
                          <label className="field">
                            <span>Ziel-Lagerplatz</span>
                            <input
                              list="location-codes-inbound"
                              value={receive.locationCode}
                              onChange={(e) =>
                                setReceive({ ...receive, locationCode: e.target.value.toUpperCase() })
                              }
                              placeholder="Platz scannen"
                            />
                            <datalist id="location-codes-inbound">
                              {locations.map((location) => (
                                <option key={location.id} value={location.code} />
                              ))}
                            </datalist>
                          </label>
                          {line.requires_lot && (
                            <TextField
                              label="Charge"
                              value={receive.lotCode}
                              onChange={(v) => setReceive({ ...receive, lotCode: v })}
                            />
                          )}
                          {line.requires_best_before && (
                            <TextField
                              label="MHD"
                              type="date"
                              value={receive.bestBefore}
                              onChange={(v) => setReceive({ ...receive, bestBefore: v })}
                            />
                          )}
                          {line.requires_serial && (
                            <TextField
                              label="Seriennummer"
                              value={receive.serial}
                              onChange={(v) => setReceive({ ...receive, serial: v })}
                            />
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button className="primary" onClick={() => confirmReceive(line)} disabled={busy}>
                            {busy ? 'Buchen …' : 'Annehmen und einlagern'}
                          </button>
                          <button onClick={() => setReceiveFor(null)}>Abbrechen</button>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
            </>
          )}
        </Card>
      )}
    </>
  );
}
